/** Largest page trip-manager returns for one `tripAnnotations` call. */
export const ANNOTATION_PAGE_SIZE = 500

/**
 * Most pages `fetchAllPages` requests, the first included: 50,000 rows at the
 * page size above. Well past INT's 41,498-row backfill, and a stop for a list
 * that never ends. It bounds requests, not rows, so overlapping pages cannot
 * stretch it.
 */
export const MAX_ANNOTATION_PAGES = 100

/**
 * Appends a newer-to-older page onto the rows already loaded, dropping any row
 * that is already there.
 *
 * Paging is by offset over a newest-first list, so an annotation recorded
 * while the pages are loading shifts every later row down by one and the next
 * page repeats the last row of the previous one. Without the drop, that row
 * would be counted twice in the table and the weekly chart.
 */
export const appendAnnotationPage = <T extends { annotationId: string }>(
  loaded: readonly T[],
  page: readonly T[],
): T[] => {
  const seen = new Set(loaded.map(row => row.annotationId))
  return [...loaded, ...page.filter(row => !seen.has(row.annotationId))]
}

export type AnnotationListSignature = {
  count: number
  firstId: string | undefined
  lastId: string | undefined
}

export const annotationListSignature = (
  rows: readonly { annotationId: string }[],
): AnnotationListSignature => ({
  count: rows.length,
  firstId: rows[0]?.annotationId,
  lastId: rows.at(-1)?.annotationId,
})

/**
 * Whether `rows` is the list `previous` described, possibly with more pages
 * appended, rather than a fresh base page.
 *
 * Appending pages keeps the first row and the row at the old end where they
 * were. A refetch swaps in the newest page alone, so an annotation recorded in
 * between moves the first row or pushes the old last row out of the window, and
 * a shorter list means the older pages are gone. Comparing both ends catches a
 * refetch that returns the same number of rows, which a count alone cannot.
 */
export const isSameAnnotationList = (
  previous: AnnotationListSignature,
  rows: readonly { annotationId: string }[],
): boolean =>
  rows.length >= previous.count &&
  rows[0]?.annotationId === previous.firstId &&
  rows[previous.count - 1]?.annotationId === previous.lastId
